import React, { useEffect, useState } from "react";
import { useAuth } from "../../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import { FileText, Calendar, Users, Filter } from "lucide-react";

const SupplierPayment = () => {
  const { requestWithAuth } = useAuth();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateRangeValue, setDateRangeValue] = useState("Last 30 Days");
  const [showDateRanges, setShowDateRanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gettingSuppliers, setGettingSuppliers] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState("Consolidated");

  const getDateRange = (value) => {
    setDateRangeValue(value);
    let start_date = "";
    let end_date = "";

    if (value === "Today") {
      setShowDateRanges(false);
      start_date = formatDate(new Date());
      end_date = formatDate(new Date());
    } else if (value === "Yesterday") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 1))
      );
    } else if (value === "Last 7 Days") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 7))
      );
      end_date = formatDate(new Date());
    } else if (value === "Last 30 Days") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setDate(new Date().getDate() - 30))
      );
      end_date = formatDate(new Date());
    } else if (value === "This Month") {
      setShowDateRanges(false);
      start_date = formatDate(new Date(new Date().setDate(1)));
      end_date = formatDate(new Date());
    } else if (value === "Last Month") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setMonth(new Date().getMonth() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setMonth(new Date().getMonth() - 1))
      );
    } else if (value === "This Year") {
      setShowDateRanges(false);
      start_date = formatDate(new Date(new Date().setMonth(0)));
      end_date = formatDate(new Date());
    } else if (value === "Last Year") {
      setShowDateRanges(false);
      start_date = formatDate(
        new Date(new Date().setFullYear(new Date().getFullYear() - 1))
      );
      end_date = formatDate(
        new Date(new Date().setFullYear(new Date().getFullYear() - 1))
      );
    } else if (value === "Custom") {
      setShowDateRanges(true);
      // Default to empty or current range if needed
    }

    if (value !== "Custom") {
      setStartDate(start_date);
      setEndDate(end_date);
    }
  };

  // FORMAT DATE
  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const getSuppliers = async () => {
    setGettingSuppliers(true);
    try {
      const data = await requestWithAuth(
        `/supplier?page=1&pageSize=10000000000000`,
        "GET"
      );
      setSuppliers(data?.results);
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setGettingSuppliers(false);
    }
  };

  const getCreditSales = async () => {
    window.location.href = `/report/purchases/supplier/payment/print?supplier=${supplier}&start_date=${startDate}&end_date=${endDate}`;
  };

  useEffect(() => {
    getSuppliers();
    getDateRange("Last 30 Days");
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <span>Reports</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Purchases</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText size={32} />
            Supplier Payment Report
          </h1>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Filter className="text-blue-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Report Criteria
                </h2>
                <p className="text-sm text-gray-500">
                  Select supplier and date range to generate report
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Supplier Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Users size={16} className="inline mr-2" />
                Supplier
              </label>
              <select
                onChange={(e) => setSupplier(e.target.value)}
                value={supplier}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="Consolidated">Consolidated (All Suppliers)</option>
                {suppliers.map((supplier, index) => (
                  <option key={index} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range Select */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar size={16} className="inline mr-2" />
                Date Range
              </label>
              <select
                onChange={(e) => getDateRange(e.target.value)}
                value={dateRangeValue}
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="Today">Today</option>
                <option value="Yesterday">Yesterday</option>
                <option value="Last 7 Days">Last 7 Days</option>
                <option value="Last 30 Days">Last 30 Days</option>
                <option value="This Month">This Month</option>
                <option value="Last Month">Last Month</option>
                <option value="This Year">This Year</option>
                <option value="Last Year">Last Year</option>
                <option value="Custom">Custom Range</option>
              </select>
            </div>

            {/* Custom Date Inputs */}
            {showDateRanges && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate || ""}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4 border-t border-gray-100">
              <button
                onClick={getCreditSales}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <RotatingLines
                      visible={true}
                      height="20"
                      width="20"
                      strokeColor="white"
                      strokeWidth="5"
                      animationDuration="0.75"
                    />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <FileText size={20} />
                    <span>Generate Report</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierPayment;
